import type { LeaseFormInput } from './lease-form-schema'
import { leaseFormDefaults } from './lease-form-schema'
import type { LeaseTableRow } from '@/types/leases'

/**
 * Convert a full DB row (snake_case LeaseTableRow) to form defaults.
 * Used by LeaseMutateDrawer when loading existing lease for editing.
 */
export function leaseRowToFormDefaults(lease: LeaseTableRow): LeaseFormInput {
  return {
    location_id: lease.location_id,
    client_id: lease.client_id,
    contact_id: lease.contact_id,
    unit_id: lease.unit_id,
    type: lease.type,
    status: lease.status,
    start_date: lease.start_date,
    end_date: lease.end_date,
    term_months: lease.term_months,
    monthly_rate: lease.monthly_rate,
    rate_basis: lease.rate_basis,
    security_deposit: lease.security_deposit,
    escalation_type: lease.escalation_type,
    escalation_pct: lease.escalation_pct,
    escalation_date: lease.escalation_date,
    auto_renew: lease.auto_renew,
    renewal_notice_days: lease.renewal_notice_days,
    allow_transient_sublease: lease.allow_transient_sublease,
    transient_revenue_share_pct: lease.transient_revenue_share_pct,
    fuel_type: lease.fuel_type,
    fuel_commitment_gallons_monthly: lease.fuel_commitment_gallons_monthly,
    fuel_commitment_gallons_annual: lease.fuel_commitment_gallons_annual,
    fuel_price_per_gallon: lease.fuel_price_per_gallon,
    fuel_price_type: lease.fuel_price_type,
    fuel_price_differential: lease.fuel_price_differential,
    placement_guarantee: lease.placement_guarantee,
    requires_unblocked_access: lease.requires_unblocked_access,
    document_url: lease.document_url,
    signed_at: lease.signed_at,
    notes: lease.notes,
  }
}

/**
 * Build renewal defaults from a full LeaseTableRow (snake_case).
 * Copies ALL terms from the original lease with new dates.
 * New start date = day after old end date (or today if no end date).
 * New end date = start + original term months (if set).
 * Status is always 'draft' for new contracts.
 */
export function buildRenewalDefaultsFromRow(row: LeaseTableRow): LeaseFormInput {
  const oldEnd = row.end_date ? new Date(row.end_date) : new Date()
  const newStart = new Date(oldEnd)
  newStart.setDate(newStart.getDate() + 1)

  let newEnd: string | null = null
  if (row.term_months) {
    // Use explicit term_months
    const endDate = new Date(newStart)
    endDate.setMonth(endDate.getMonth() + row.term_months)
    newEnd = endDate.toISOString().split('T')[0]
  } else if (row.start_date && row.end_date) {
    // Derive period from original date range
    const oldStart = new Date(row.start_date)
    const diffMs = oldEnd.getTime() - oldStart.getTime()
    const endDate = new Date(newStart.getTime() + diffMs)
    newEnd = endDate.toISOString().split('T')[0]
  }

  return {
    ...leaseFormDefaults,
    location_id: row.location_id,
    client_id: row.client_id,
    contact_id: row.contact_id,
    unit_id: row.unit_id,
    type: row.type,
    status: 'draft',
    start_date: newStart.toISOString().split('T')[0],
    end_date: newEnd,
    term_months: row.term_months,
    monthly_rate: row.monthly_rate,
    rate_basis: row.rate_basis,
    security_deposit: row.security_deposit,
    escalation_type: row.escalation_type,
    escalation_pct: row.escalation_pct,
    auto_renew: row.auto_renew,
    renewal_notice_days: row.renewal_notice_days,
    allow_transient_sublease: row.allow_transient_sublease,
    transient_revenue_share_pct: row.transient_revenue_share_pct,
    fuel_type: row.fuel_type,
    fuel_commitment_gallons_monthly: row.fuel_commitment_gallons_monthly,
    fuel_commitment_gallons_annual: row.fuel_commitment_gallons_annual,
    fuel_price_per_gallon: row.fuel_price_per_gallon,
    fuel_price_type: row.fuel_price_type,
    fuel_price_differential: row.fuel_price_differential,
    placement_guarantee: row.placement_guarantee,
    requires_unblocked_access: row.requires_unblocked_access,
  }
}
